import { useEffect, useState, useRef, useCallback } from 'react';
import { FiCopy } from 'react-icons/fi';
import { BsPatchCheckFill } from 'react-icons/bs';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import { useLoadItems } from './useLoadItems';
import './style.scss';

const COPIED_RESET_MS = 2000;

interface Props {
  type?: string;
  selectedKey?: string;
  keyword?: string;
  category?: string;
  endpoint?: string;
  searchCurrentLayer?: (keyword: string) => Promise<string[]>;
  setSearchContent: (item: string) => void;
  setSearchFocused: (item: boolean) => void;
  onSelectSearchDetailsItem: (item: string) => void;
  setTotalItems: (total: number) => void;
  onSearchResultsChange?: (items: string[]) => void;
}

function isNil(value: any): boolean {
  return value === undefined || value === null;
}

const SearchResultList: React.FC<Props> = ({
  type,
  selectedKey,
  keyword,
  category,
  endpoint,
  searchCurrentLayer,
  setSearchContent,
  setSearchFocused,
  onSelectSearchDetailsItem,
  setTotalItems,
  onSearchResultsChange,
}) => {
  const { loading, items, paginationData, hasNextPage, error, loadMore } =
    useLoadItems(type, selectedKey, keyword, category, searchCurrentLayer);

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

  const handleCopy = useCallback(
    (e: React.MouseEvent, item: string, index: number) => {
      e.stopPropagation();
      if (item) {
        navigator.clipboard.writeText(item);
        setCopiedIndex(index);
        setTimeout(() => setCopiedIndex(null), COPIED_RESET_MS);
      }
    },
    [],
  );

  useEffect(() => {
    setTotalItems(paginationData.totalItems);
  }, [paginationData.totalItems]);

  useEffect(() => {
    onSearchResultsChange?.(items);
  }, [items, onSearchResultsChange]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [items, keyword]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (items.length === 0) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        e.stopPropagation();
        setSelectedIndex((i) => Math.min(i + 1, items.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        e.stopPropagation();
        setSelectedIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === 'Enter') {
        const item = items[selectedIndex];
        if (item != null) {
          e.preventDefault();
          e.stopPropagation();
          setSearchContent(item);
          setSearchFocused(false);
          onSelectSearchDetailsItem(item);
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [items, selectedIndex, setSearchContent, setSearchFocused, onSelectSearchDetailsItem]);

  useEffect(() => {
    itemRefs.current[selectedIndex]?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [selectedIndex]);

  const [sentryRef] = useInfiniteScroll({
    loading,
    hasNextPage,
    onLoadMore: loadMore,
    // When there is an error, we stop infinite loading.
    // It can be reactivated by setting "error" state as undefined.
    disabled: !!error,
    // `rootMargin` is passed to `IntersectionObserver`.
    // We can use it to trigger 'onLoadMore' when the sentry comes near to become
    // visible, instead of becoming fully visible on the screen.
    rootMargin: '0px 0px 400px 0px',
  });

  const showNotFound =
    !isNil(type) &&
    !isNil(keyword) &&
    (keyword ?? '').trim() !== '' &&
    !loading &&
    !hasNextPage &&
    paginationData.totalItems === 0;

  return (
    <div className="xa-search-results">
      {!isNil(type) && !isNil(keyword) && paginationData.totalItems > 0 && (
        <div className="xa-search-results-title">
          Search Results: {paginationData.totalItems}
        </div>
      )}
      {showNotFound && (
        <div className="xa-search-results-not-found">Not found</div>
      )}
      {items.map((item, index) => (
        <div
          key={index}
          ref={(el) => { itemRefs.current[index] = el; }}
          title="Right click to copy"
          onClick={() => {
            setSearchContent(item);
            setSearchFocused(false);
            onSelectSearchDetailsItem(item);
          }}
          onContextMenu={(e) => {
            e.preventDefault();
            handleCopy(e, item, index);
          }}
          onMouseEnter={() => setSelectedIndex(index)}
          className={`xa-search-results-item${index === selectedIndex ? ' selected' : ''}`}
        >
          <span className="xa-search-results-item-text">{item}</span>
          {copiedIndex === index && (
            <span className="xa-search-results-copied-notice">Copied!</span>
          )}
          <button
            type="button"
            className="xa-search-results-copy-btn"
            onClick={(e) => handleCopy(e, item, index)}
            title="Copy"
            aria-label="Copy"
          >
            {copiedIndex === index ? (
              <BsPatchCheckFill className="xa-search-results-copy-btn-check" />
            ) : (
              <FiCopy />
            )}
          </button>
        </div>
      ))}
      {(loading || hasNextPage) && (
        <div
          key="loading-item"
          ref={sentryRef}
          className="xa-search-results-item"
        >
          Loading...
        </div>
      )}
    </div>
  );
};

export default SearchResultList;
